<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- 標題和操作區域 -->
        <div class="d-flex flex-wrap flex-lg-nowrap align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <h3 class="font-weight-bolder text-nowrap mb-0">
              <feather-icon size="18" icon="FileTextIcon" />
              其他法庭案件記錄
            </h3>
            <div class="form-col-select ml-2">
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
              />
              <feather-icon size="18" icon="ChevronDownIcon" />
            </div>
          </div>

          <div class="d-flex align-items-center">
            <!-- 搜尋框 -->
            <div class="search-primary mr-2">
              <feather-icon size="16" icon="SearchIcon" />
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="搜尋案件編號、當事人..."
              />
            </div>

            <!-- 日期篩選 -->
            <b-form-input
              v-model="selectedDate"
              type="date"
              class="date-selector mr-2"
              style="width: 150px;"
            />

            <!-- 法庭篩選 -->
            <v-select
              v-model="selectedCourt"
              :options="courtOptions"
              :clearable="true"
              placeholder="選擇法庭"
              class="court-selector mr-2"
            />

            <!-- 重新整理按鈕 -->
            <b-button
              variant="outline-primary"
              @click="refreshData"
              :disabled="loading"
              class="mr-2"
            >
              <feather-icon size="16" icon="RefreshCwIcon" class="mr-1" />
              重新整理
            </b-button>

            <!-- 新增按鈕 -->
            <b-button
              variant="primary"
              @click="openAddModal"
            >
              <feather-icon size="16" icon="PlusIcon" class="mr-1" />
              新增案件
            </b-button>
          </div>
        </div>
      </div>


      <!-- 資料表格 -->
      <b-table
        ref="otherCourtCasesTable"
        :items="fetchRecords"
        :fields="tableColumns"
        :busy="loading"
        responsive
        show-empty
        empty-text="未找到匹配的案件"
        table-class="court-cases-table"
      >
        <!-- 載入狀態 -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-2" />
            <strong>載入中...</strong>
          </div>
        </template>

        <!-- 案件日期欄位 -->
        <template #cell(case_date)="data">
          <b-badge variant="light-primary" class="font-weight-semibold">
            {{ formatDate(data.item.case_date) }}
          </b-badge>
        </template>

        <!-- 案件編號欄位 -->
        <template #cell(case_number)="data">
          <strong class="text-primary" style="font-size: 0.857rem;">
            {{ data.item.case_number }}
          </strong>
        </template>

        <!-- 案件類型欄位 -->
        <template #cell(case_type)="data">
          <b-badge
            :variant="'light-' + getCaseTypeVariant(data.item.case_type)"
            class="text-capitalize font-weight-semibold"
          >
            {{ truncateText(data.item.case_type, 20) }}
          </b-badge>
        </template>

        <!-- 法庭欄位 -->
        <template #cell(court)="data">
          <div class="court-cell">
            <b-badge variant="light-secondary" class="mb-25">{{ data.item.court_code }}</b-badge>
            <div class="text-truncate small" :title="data.item.court">
              {{ truncateText(data.item.court, 25) }}
            </div>
          </div>
        </template>

        <!-- 法官欄位 -->
        <template #cell(judge)="data">
          <div class="text-truncate" :title="data.item.judge || '未指定'">
            <span class="font-weight-semibold">{{ truncateText(data.item.judge, 30) }}</span>
          </div>
        </template>

        <!-- 控罪欄位 -->
        <template #cell(charge)="data">
          <div class="text-truncate" :title="data.item.charge || '未指定'">
            <span class="font-weight-semibold">{{ truncateText(data.item.charge, 20) }}</span>
          </div>
        </template>

        <!-- 訴訟各方欄位 -->
        <template #cell(parties)="data">
          <div class="text-truncate" :title="data.item.parties">
            <span class="text-body">{{ truncateText(data.item.parties, 40) }}</span>
          </div>
        </template>

        <!-- 應訊代表欄位 -->
        <template #cell(representatives)="data">
          <div class="text-truncate" :title="data.item.representatives || '未指定'">
            <span class="text-muted">{{ truncateText(data.item.representatives, 40) }}</span>
          </div>
        </template>

        <!-- 操作欄位 -->
        <template #cell(actions)="data">
          <div class="text-center">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="viewCase(data.item)">
                <feather-icon icon="EyeIcon" />
                <span class="align-middle ml-50">檢視詳情</span>
              </b-dropdown-item>
              <b-dropdown-item @click="editCase(data.item)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">編輯</span>
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="deleteCase(data.item)" variant="danger">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">刪除</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <!-- 分頁 -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">顯示 {{ (currentPage - 1) * (perPage && perPage.value || 15) + 1 }} 到 {{ Math.min(currentPage * (perPage && perPage.value || 15), totalRecords) }} 筆，共 {{ totalRecords }} 筆記錄</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage && perPage.value || 15"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- 案件詳情 Modal -->
    <b-modal
      id="case-detail-modal"
      v-model="showDetailModal"
      title="案件詳情"
      size="lg"
      hide-footer
      scrollable
    >
      <div v-if="selectedCase">
        <b-row>
          <b-col cols="12">
            <h5 class="mb-3">基本資訊</h5>
          </b-col>
          <b-col lg="6" cols="12">
            <div class="mb-2">
              <strong>案件編號：</strong>
              <span class="text-primary">{{ selectedCase.case_number }}</span>
            </div>
            <div class="mb-2">
              <strong>案件日期：</strong>
              {{ formatDate(selectedCase.case_date) }}
            </div>
            <div class="mb-2">
              <strong>案件類型：</strong>
              <b-badge :variant="getCaseTypeVariant(selectedCase.case_type)">
                {{ selectedCase.case_type || '未指定' }}
              </b-badge>
            </div>
          </b-col>
          <b-col lg="6" cols="12">
            <div class="mb-2">
              <strong>法庭：</strong>
              {{ selectedCase.court }}
            </div>
            <div class="mb-2">
              <strong>法官：</strong>
              {{ selectedCase.judge || '未指定' }}
            </div>
            <div class="mb-2">
              <strong>控罪：</strong>
              {{ selectedCase.charge || '未指定' }}
            </div>
          </b-col>
          <b-col cols="12" class="mt-3">
            <h5 class="mb-3">詳細資訊</h5>
            <div class="mb-3">
              <strong>訴訟各方：</strong>
              <p class="mt-1">{{ selectedCase.parties || '未指定' }}</p>
            </div>
            <div class="mb-3">
              <strong>應訊代表：</strong>
              <p class="mt-1">{{ selectedCase.representatives || '未指定' }}</p>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- 新增/編輯案件 Modal -->
    <b-modal
      id="case-form-modal"
      v-model="showFormModal"
      :title="isEditMode ? '編輯案件' : '新增案件'"
      size="lg"
      hide-footer
      scrollable
      @hidden="resetForm"
    >
      <b-form @submit.prevent="submitForm">
        <b-row>
          <b-col lg="6" cols="12">
            <b-form-group label="案件日期 *" label-for="case_date">
              <b-form-input
                id="case_date"
                v-model="formData.case_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="12">
            <b-form-group label="案件編號 *" label-for="case_number">
              <b-form-input
                id="case_number"
                v-model="formData.case_number"
                placeholder="輸入案件編號"
                required
              />
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="12">
            <b-form-group label="法庭代碼 *" label-for="court_code">
              <v-select
                id="court_code"
                v-model="formData.court_code"
                :options="courtCodeOptions"
                placeholder="選擇法庭代碼"
                :reduce="option => option.value"
              />
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="12">
            <b-form-group label="法庭名稱 *" label-for="court">
              <b-form-input
                id="court"
                v-model="formData.court"
                placeholder="輸入法庭名稱"
                required
              />
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="12">
            <b-form-group label="案件類型" label-for="case_type">
              <b-form-input
                id="case_type"
                v-model="formData.case_type"
                placeholder="輸入案件類型"
              />
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="12">
            <b-form-group label="法官" label-for="judge">
              <b-form-input
                id="judge"
                v-model="formData.judge"
                placeholder="輸入法官姓名"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="控罪" label-for="charge">
              <b-form-textarea
                id="charge"
                v-model="formData.charge"
                placeholder="輸入控罪內容"
                rows="2"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="訴訟各方 *" label-for="parties">
              <b-form-textarea
                id="parties"
                v-model="formData.parties"
                placeholder="輸入訴訟各方資訊"
                rows="3"
                required
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="應訊代表" label-for="representatives">
              <b-form-textarea
                id="representatives"
                v-model="formData.representatives"
                placeholder="輸入應訊代表資訊"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="outline-secondary"
            @click="showFormModal = false"
            class="mr-2"
          >
            取消
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="saveLoading"
          >
            <b-spinner v-if="saveLoading" small class="mr-1" />
            {{ isEditMode ? '更新' : '建立' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- 刪除確認 Modal -->
    <b-modal
      id="delete-confirm-modal"
      v-model="showDeleteModal"
      title="確認刪除"
      hide-footer
      modal-class="modal-danger"
    >
      <p class="mb-0">
        確定要刪除案件「<strong>{{ caseToDelete && caseToDelete.case_number }}</strong>」嗎？
        <br>
        <small class="text-muted">此操作無法復原。</small>
      </p>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          @click="showDeleteModal = false"
          class="mr-2"
        >
          取消
        </b-button>
        <b-button
          variant="danger"
          @click="confirmDelete"
          :disabled="deleteLoading"
        >
          <b-spinner v-if="deleteLoading" small class="mr-1" />
          確認刪除
        </b-button>
      </div>
    </b-modal>

    <!-- 保存 Loading Overlay -->
    <b-overlay
      :show="saveLoading"
      no-wrap
      opacity="0.85"
      blur="2px"
    >
      <template #overlay>
        <div class="d-flex flex-column align-items-center">
          <b-spinner variant="primary" style="width: 4rem; height: 4rem;" />
          <p class="mt-3 text-white font-weight-bold">{{ isEditMode ? '更新中...' : '建立中...' }}</p>
        </div>
      </template>
    </b-overlay>

    <!-- 刪除 Loading Overlay -->
    <b-overlay
      :show="deleteLoading"
      no-wrap
      opacity="0.85"
      blur="2px"
    >
      <template #overlay>
        <div class="d-flex flex-column align-items-center">
          <b-spinner variant="danger" style="width: 4rem; height: 4rem;" />
          <p class="mt-3 text-white font-weight-bold">刪除中...</p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BFormTextarea,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  BSpinner,
  BModal,
  BForm,
  BOverlay
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import { ref, computed, onMounted, watch } from '@vue/composition-api'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BSpinner,
    BModal,
    BForm,
    BOverlay,
    vSelect
  },
  setup(props, { root }) {
    // 響應式數據
    const loading = ref(false)
    const saveLoading = ref(false)
    const deleteLoading = ref(false)
    const otherCourtCasesTable = ref(null)
    const records = ref([])
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPage = ref({ label: '15', value: 15 })
    const searchQuery = ref('')
    const selectedDate = ref('')
    const selectedCourt = ref(null)
    const selectedCase = ref(null)
    const showDetailModal = ref(false)
    const showFormModal = ref(false)
    const showDeleteModal = ref(false)
    const isEditMode = ref(false)
    const caseToDelete = ref(null)

    const formData = ref({
      case_date: '',
      case_number: '',
      court: '',
      court_code: null,
      case_type: '',
      judge: '',
      charge: '',
      parties: '',
      representatives: ''
    })

    // 配置選項
    const perPageOptions = [
      { label: '10', value: 10 },
      { label: '15', value: 15 },
      { label: '25', value: 25 },
      { label: '50', value: 50 },
      { label: '100', value: 100 }
    ]

    const courtOptions = [
      { label: 'CFA - 終審法院', value: 'CFA' },
      { label: 'CACFI - 高等法院 (上訴法庭及原訟法庭)', value: 'CACFI' },
      { label: 'HCMC - 高等法院 (聆案官聆訊案件表)', value: 'HCMC' },
      { label: 'MCL - 高等法院 (三分鐘內庭聆訊表)', value: 'MCL' },
      { label: 'BP - 高等法院 (破產呈請)', value: 'BP' },
      { label: 'CLPI - 高等法院 (內庭聆訊表 (人身傷亡案件))', value: 'CLPI' },
      { label: 'CLCMC - 高等法院 (核對列表聆訊 / 案件管理會議)', value: 'CLCMC' },
      { label: 'CRHPI - 高等法院 (核對列表審核聆訊 (人身傷亡案件))', value: 'CRHPI' },
      { label: 'CWUP - 高等法院 (公司清盤呈請)', value: 'CWUP' },
      { label: 'MIA - 高等法院 (有關無力償還的雜項申請)', value: 'MIA' },
      { label: 'OTD - 高等法院 (反對自動解除破產)', value: 'OTD' },
      { label: 'AJSL - 高等法院 (委任共同及各別的清盤人)', value: 'AJSL' },
      { label: 'O14 - 高等法院 (簡易判決)', value: 'O14' },
      { label: 'CT - 競爭事務審裁處', value: 'CT' },
      { label: 'DC - 區域法院', value: 'DC' },
      { label: 'DCMC - 區域法院 (聆案官聆訊案件表)', value: 'DCMC' },
      { label: 'FMC - 家事法庭', value: 'FMC' },
      { label: 'LANDS - 土地審裁處', value: 'LANDS' },
      { label: 'ETNMAG - 東區裁判法院', value: 'ETNMAG' },
      { label: 'KCMAG - 九龍城裁判法院', value: 'KCMAG' },
      { label: 'KTMAG - 觀塘裁判法院', value: 'KTMAG' },
      { label: 'WKMAG - 西九龍裁判法院', value: 'WKMAG' },
      { label: 'STMAG - 沙田裁判法院', value: 'STMAG' },
      { label: 'FLMAG - 粉嶺裁判法院', value: 'FLMAG' },
      { label: 'TMMAG - 屯門裁判法院', value: 'TMMAG' },
      { label: 'ALLMAG - 所有裁判法院', value: 'ALLMAG' },
      { label: 'LT - 勞資審裁處', value: 'LT' },
      { label: 'SMT - 小額錢債審裁處', value: 'SMT' },
      { label: 'OAT - 淫褻物品審裁處', value: 'OAT' },
      { label: 'CRC - 死因裁判法庭', value: 'CRC' }
    ]

    const courtCodeOptions = [
      { label: 'CFA - 終審法院', value: 'CFA' },
      { label: 'CACFI - 高等法院 (上訴法庭及原訟法庭)', value: 'CACFI' },
      { label: 'HCMC - 高等法院 (聆案官聆訊案件表)', value: 'HCMC' },
      { label: 'MCL - 高等法院 (三分鐘內庭聆訊表)', value: 'MCL' },
      { label: 'BP - 高等法院 (破產呈請)', value: 'BP' },
      { label: 'CLPI - 高等法院 (內庭聆訊表 (人身傷亡案件))', value: 'CLPI' },
      { label: 'CLCMC - 高等法院 (核對列表聆訊 / 案件管理會議)', value: 'CLCMC' },
      { label: 'CRHPI - 高等法院 (核對列表審核聆訊 (人身傷亡案件))', value: 'CRHPI' },
      { label: 'CWUP - 高等法院 (公司清盤呈請)', value: 'CWUP' },
      { label: 'MIA - 高等法院 (有關無力償還的雜項申請)', value: 'MIA' },
      { label: 'OTD - 高等法院 (反對自動解除破產)', value: 'OTD' },
      { label: 'AJSL - 高等法院 (委任共同及各別的清盤人)', value: 'AJSL' },
      { label: 'O14 - 高等法院 (簡易判決)', value: 'O14' },
      { label: 'CT - 競爭事務審裁處', value: 'CT' },
      { label: 'DC - 區域法院', value: 'DC' },
      { label: 'DCMC - 區域法院 (聆案官聆訊案件表)', value: 'DCMC' },
      { label: 'FMC - 家事法庭', value: 'FMC' },
      { label: 'LANDS - 土地審裁處', value: 'LANDS' },
      { label: 'ETNMAG - 東區裁判法院', value: 'ETNMAG' },
      { label: 'KCMAG - 九龍城裁判法院', value: 'KCMAG' },
      { label: 'KTMAG - 觀塘裁判法院', value: 'KTMAG' },
      { label: 'WKMAG - 西九龍裁判法院', value: 'WKMAG' },
      { label: 'STMAG - 沙田裁判法院', value: 'STMAG' },
      { label: 'FLMAG - 粉嶺裁判法院', value: 'FLMAG' },
      { label: 'TMMAG - 屯門裁判法院', value: 'TMMAG' },
      { label: 'ALLMAG - 所有裁判法院', value: 'ALLMAG' },
      { label: 'LT - 勞資審裁處', value: 'LT' },
      { label: 'SMT - 小額錢債審裁處', value: 'SMT' },
      { label: 'OAT - 淫褻物品審裁處', value: 'OAT' },
      { label: 'CRC - 死因裁判法庭', value: 'CRC' }
    ]

    // 表格欄位配置
    const tableColumns = [
      {
        key: 'case_date',
        label: '案件日期',
        sortable: true,
        thStyle: { width: '110px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'case_number',
        label: '案件編號',
        sortable: true,
        thStyle: { width: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'case_type',
        label: '案件類型',
        sortable: true,
        thStyle: { width: '120px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'court',
        label: '法庭',
        sortable: true,
        thStyle: { width: '160px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'judge',
        label: '法官',
        sortable: true,
        thStyle: { width: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'charge',
        label: '控罪',
        sortable: false,
        thStyle: { width: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'parties',
        label: '訴訟各方',
        sortable: false,
        thStyle: { width: '200px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'representatives',
        label: '應訊代表',
        sortable: false,
        thStyle: { width: '200px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'actions',
        label: '操作',
        sortable: false,
        thClass: 'text-center',
        thStyle: { width: '80px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      }
    ]

    // API 方法
    const getToken = () => localStorage.getItem('accessToken')

    const fetchRecords = async (ctx) => {
      loading.value = true
      try {
        const token = getToken()
        const params = {
          page: ctx && ctx.currentPage || currentPage.value,
          per_page: perPage.value && perPage.value.value || 15,
          q: searchQuery.value || null,
          date: selectedDate.value || null,
          court_code: selectedCourt.value && selectedCourt.value.value || null
        }

        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}/other-court-cases`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data.success) {
          const data = response.data.data
          totalRecords.value = data.total
          return data.data || []
        }
        return []
      } catch (error) {
        console.error('獲取案件記錄失敗:', error)
        root.$toast({
          component: 'ToastificationContent',
          props: {
            title: '錯誤',
            text: '獲取案件記錄失敗，請重試',
            variant: 'danger'
          }
        })
        return []
      } finally {
        loading.value = false
      }
    }

    const submitForm = async () => {
      saveLoading.value = true
      try {
        const token = getToken()
        const url = isEditMode.value
          ? `${process.env.VUE_APP_SERVICE_URL}/other-court-cases/${selectedCase.value.id}`
          : `${process.env.VUE_APP_SERVICE_URL}/other-court-cases`

        const method = isEditMode.value ? 'put' : 'post'

        const response = await axios[method](
          url,
          formData.value,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data.success) {
          root.$toast({
            component: 'ToastificationContent',
            props: {
              title: '成功',
              text: isEditMode.value ? '案件更新成功' : '案件建立成功',
              variant: 'success'
            }
          })
          showFormModal.value = false
          refreshData()
        } else {
          const errorMessage = response.data.errors
            ? Object.values(response.data.errors).flat().join(', ')
            : response.data.message || '操作失敗'
          root.$toast({
            component: 'ToastificationContent',
            props: {
              title: '錯誤',
              text: errorMessage,
              variant: 'danger'
            }
          })
        }
      } catch (error) {
        console.error('提交失敗:', error)
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : '操作失敗，請重試'
        root.$toast({
          component: 'ToastificationContent',
          props: {
            title: '錯誤',
            text: errorMessage,
            variant: 'danger'
          }
        })
      } finally {
        saveLoading.value = false
      }
    }

    const confirmDelete = async () => {
      deleteLoading.value = true
      try {
        const token = getToken()
        const response = await axios.delete(
          `${process.env.VUE_APP_SERVICE_URL}/other-court-cases/${caseToDelete.value.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data.success) {
          root.$toast({
            component: 'ToastificationContent',
            props: {
              title: '成功',
              text: '案件刪除成功',
              variant: 'success'
            }
          })
          showDeleteModal.value = false
          refreshData()
        } else {
          root.$toast({
            component: 'ToastificationContent',
            props: {
              title: '錯誤',
              text: response.data.message || '刪除失敗',
              variant: 'danger'
            }
          })
        }
      } catch (error) {
        console.error('刪除失敗:', error)
        root.$toast({
          component: 'ToastificationContent',
          props: {
            title: '錯誤',
            text: '刪除失敗，請重試',
            variant: 'danger'
          }
        })
      } finally {
        deleteLoading.value = false
      }
    }

    // UI 方法
    const refreshData = () => {
      root.$nextTick(() => {
        if (otherCourtCasesTable.value) {
          otherCourtCasesTable.value.refresh()
        }
      })
    }

    const openAddModal = () => {
      isEditMode.value = false
      resetForm()
      showFormModal.value = true
    }

    const viewCase = (caseItem) => {
      selectedCase.value = caseItem
      showDetailModal.value = true
    }

    const editCase = (caseItem) => {
      isEditMode.value = true
      selectedCase.value = caseItem
      formData.value = {
        case_date: caseItem.case_date,
        case_number: caseItem.case_number,
        court: caseItem.court,
        court_code: caseItem.court_code,
        case_type: caseItem.case_type || '',
        judge: caseItem.judge || '',
        charge: caseItem.charge || '',
        parties: caseItem.parties,
        representatives: caseItem.representatives || ''
      }
      showFormModal.value = true
    }

    const deleteCase = (caseItem) => {
      caseToDelete.value = caseItem
      showDeleteModal.value = true
    }

    const resetForm = () => {
      formData.value = {
        case_date: '',
        case_number: '',
        court: '',
        court_code: null,
        case_type: '',
        judge: '',
        charge: '',
        parties: '',
        representatives: ''
      }
    }

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-TW')
    }

    const getCaseTypeVariant = (type) => {
      const typeMap = {
        '破產呈請': 'warning',
        '商業案件': 'primary',
        '民事案件': 'info',
        '刑事案件': 'danger'
      }
      return typeMap[type] || 'secondary'
    }

    const truncateText = (text, limit) => {
      if (!text) return '未指定'
      return text.length > limit ? text.substring(0, limit) + '...' : text
    }

    // 監聽器
    let searchTimeout = null
    watch([searchQuery, selectedDate, selectedCourt], () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        currentPage.value = 1
        root.$nextTick(() => {
          if (otherCourtCasesTable.value) {
            otherCourtCasesTable.value.refresh()
          }
        })
      }, 500)
    })

    watch(perPage, () => {
      currentPage.value = 1
      root.$nextTick(() => {
        if (otherCourtCasesTable.value) {
          otherCourtCasesTable.value.refresh()
        }
      })
    })

    watch(currentPage, () => {
      root.$nextTick(() => {
        if (otherCourtCasesTable.value) {
          otherCourtCasesTable.value.refresh()
        }
      })
    })

    return {
      // 響應式數據
      loading,
      saveLoading,
      deleteLoading,
      otherCourtCasesTable,
      records,
      totalRecords,
      currentPage,
      perPage,
      searchQuery,
      selectedDate,
      selectedCourt,
      selectedCase,
      showDetailModal,
      showFormModal,
      showDeleteModal,
      isEditMode,
      caseToDelete,
      formData,

      // 配置
      perPageOptions,
      courtOptions,
      courtCodeOptions,
      tableColumns,

      // 方法
      fetchRecords,
      submitForm,
      confirmDelete,
      refreshData,
      openAddModal,
      viewCase,
      editCase,
      deleteCase,
      resetForm,
      formatDate,
      getCaseTypeVariant,
      truncateText
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.search-primary {
  position: relative;

  .feather-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
  }

  .form-control {
    padding-left: 2.2rem;
    width: 200px;
  }
}

.form-col-select {
  position: relative;

  .feather-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
    pointer-events: none;
  }
}

.per-page-selector {
  width: 90px;
}

.date-selector, .court-selector {
  width: 150px;
}

.court-cases-table {
  th, td {
    border-color: #ebe9f1;
    padding: 0.75rem 0.5rem;
  }

  th {
    background-color: #f8f8f8;
    font-weight: 600;
    font-size: 0.857rem;
    white-space: nowrap;
  }

  td {
    font-size: 0.857rem;
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.6rem;
  }
}

.court-cell {
  .badge {
    display: inline-block;
    margin-bottom: 0.25rem;
  }
  .small {
    font-size: 0.75rem;
    color: #6e6b7b;
    line-height: 1.2;
  }
}

.text-wrap {
  word-wrap: break-word;
  white-space: normal;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-danger .modal-header {
  background-color: #ea5455;
  color: white;
}
</style>
